import React, { useState, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { Modal, Spinner } from "react-bootstrap"
import Select from "react-select"
import { toast } from "react-toastify"
import ReactQuill from "react-quill"
import { sendReleasedFiles, sendReleasedFilesSms } from "../../../../../requesters/jobServiceRequester"
import ZipContentsPreview from "./ZipContentsPreview"
import { getBaseUrl } from "../../../reducers"
import "react-quill/dist/quill.snow.css"
import {
  PLAN_OF_DELIMITATION_RELEASING_CONTENT,
  PLAN_OF_REDEFINITION_OR_PLAN_OF_CONSOLIDATION_RELEASING_CONTENT,
  PLAN_OF_SUBDIVISION_RELEASING_CONTENT,
  _88E_OR_88E_EASE_RELEASING_CONTENT,
  isSetProductForDocketCheckout,
  isSetoutProduct,
  attachesReleasedFiles,
  isReleasedFilesSmsProduct
} from "../../../../../utils/constants"
import { excludeParticipantAccessContacts } from "../../../../../utils/jobContacts"

const SendReleasedFilesEmailModal = ({
  show, onHide, jobService, file
}) => {
  const {
    attributes: {
      toContactOptions,
      toSmsContactOptions = [],
      ccContactOptions,
      criticalNote,
      productName,
      code,
      jobNo,
      forProjectX,
      projectXJobNo,
      dropboxFileUrl,
      outstandingInvoiceFlagged,
      unmatchedInvoiceFlagged,
      defaultFileRelease,
      releasedFiles = []
    }
  } = jobService
  const displayJobNo = forProjectX ? projectXJobNo : jobNo
  const baseUrl = useSelector(getBaseUrl)

  const ccContactOptionsForModal = useMemo(
    () => excludeParticipantAccessContacts(ccContactOptions),
    [ccContactOptions]
  )

  const mainPersons = toContactOptions.filter(jobContact => jobContact.contactType === "Main Person")
  const invoicePersons = toContactOptions.filter(jobContact => jobContact.contactType === "Invoice")
  const defaultOption = invoicePersons.length ? invoicePersons[0] : mainPersons[0]
  const toContactOptionsList = toContactOptions.map(jobContact => ({ value: jobContact.value, label: jobContact.label }))
  const [currentContact, setCurrentContact] = useState({})

  const [contactsToBeCc, setContactsToBeCc] = useState([])
  const [displayedCcContacts, setDisplayedCcContacts] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const releasedFileOptions = releasedFiles.map(releasedFile => {
    const label = releasedFile.data.attributes.name || releasedFile.data.attributes.fileName
    const storedName = releasedFile.data.attributes.fileName || label

    return {
      id: releasedFile.data.id,
      label,
      isZip: /\.zip$/i.test(storedName || "") || /\.zip$/i.test(label || "")
    }
  })
  const [sendSms, setSendSms] = useState(false)
  const criticalNoteText = (criticalNote || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim()
  const hasCriticalNote = !!criticalNoteText
  const showSendSms = isReleasedFilesSmsProduct(code) && hasCriticalNote
  const isSpecialWAE = ["WAE-STW", "WAE-ADD"].includes(jobService.attributes?.code)
  const isPlanOfDelimitation = jobService.attributes?.code === "DELIM"
  const isPlanOfRedefinitionOrPlanOfConsolidation = ["REDEF", "CONSOL"].includes(jobService.attributes?.code)
  const isPlanOfSubdivision = jobService.attributes?.code === "SUB"
  const is_88e_Or_88e_Ease = ["88E", "88E-EASE"].includes(jobService.attributes?.code) // Define name like this to able to read
  const attachFilesToEmail = attachesReleasedFiles(code, defaultFileRelease) && !dropboxFileUrl
  const fileUrl = dropboxFileUrl ? `<a href="${dropboxFileUrl}">${dropboxFileUrl}</a>` : "{{FileURL}}"
  const additionalInformation3dProduct=["3D-ADD", "3D-ADD-DRAFT", "3D-ADD-MISS"]
  const additionalInformationProduct=["ADD-INFO", "ADD-INFO-DRAFT", "ADD-INFO-MISS", "ADD-INFO-RS", "WAE-ADD"]
  const isAddInfo3dProduct = additionalInformation3dProduct.includes(code)
  const isAddInfoProduct = additionalInformationProduct.includes(code)
  const productNameLabel = isAddInfoProduct ? "Additional Information" : isAddInfo3dProduct ? "3d Additional Information" : productName

  const filesReadyCopy = attachFilesToEmail
    ? `<p>Your files for ${productNameLabel} are attached to this email.</p>`
    : `<p>Your files for ${productNameLabel} are ready to download, please use the link below to access.${dropboxFileUrl ? "" : " For security reasons, the link will only be valid for 7 days."}</p>
    <p>${fileUrl}</p>`

  const emailTemplate = `
    <p>Hi {{ToName}},</p>
    <p>Thank you for using C&amp;A Surveyors - your trusted local land surveyors.</p>
    ${isSpecialWAE ? "<p>This is a preliminary Work as Executed (WAE) plan, it is intended for review by the hydraulic engineer. Please forward any comments or feedback from the engineer and / or client to me.</p>" : ""}
    ${criticalNote ? `<p>Please review these notes from our Registered Surveyor:</p><b><i>${criticalNote}</i></b>` : ""}
    ${filesReadyCopy}
    ${isPlanOfDelimitation ? PLAN_OF_DELIMITATION_RELEASING_CONTENT : ""}
    ${isPlanOfRedefinitionOrPlanOfConsolidation ? PLAN_OF_REDEFINITION_OR_PLAN_OF_CONSOLIDATION_RELEASING_CONTENT : ""}
    ${isPlanOfSubdivision ? PLAN_OF_SUBDIVISION_RELEASING_CONTENT : ""}
    ${is_88e_Or_88e_Ease ? _88E_OR_88E_EASE_RELEASING_CONTENT : ""}
    <p>If you have any questions regarding the job, please reply to this email or call us on (02) 9630 9299.</p>
  `;

  const smsTemplate = contactName => (
    `Hi ${contactName || "{{ToName}}"},\n`
    + "We have just released your Survey for {{ShortAddress}}, please see the following Critical Note by our Registered Surveyor:\n"
    + `${criticalNoteText}\n`
    + "Please contact us if you require further clarification."
  )

  const [message, setMessage] = useState(emailTemplate)
  const [smsMessage, setSmsMessage] = useState(smsTemplate())

  const [subject, setSubject] = useState(`${displayJobNo} - ${jobService.jobTitle}`)
  const [additionalCc, setAdditionalCc] = useState("")
  const [loading, setLoading] = useState(false)

  const toastOptions = {
    toastId: "container-inner",
    containerId: "main"
  }

  const selectedContactHasPhone = currentContact?.value
    && toSmsContactOptions.some(c => c.value === currentContact.value)

  const handleContacts = (e, contact) => {
    const tmpArray = [...contactsToBeCc]
    if (e.target.checked) {
      tmpArray.push(parseInt(contact.value, 10))
    } else {
      _.pull(tmpArray, parseInt(contact.value, 10))
    }

    setContactsToBeCc(tmpArray)
  }

  const handleFileSelection = (e, fileId) => {
    const tmpArray = [...selectedFileIds]
    if (e.target.checked) {
      tmpArray.push(fileId)
    } else {
      _.pull(tmpArray, fileId)
    }
    setSelectedFileIds(tmpArray)
  }

  const sendReleasedFilesHandler = () => {
    if (sendSms && !selectedContactHasPhone) {
      toast.error(
        <div>
          <span role="img" aria-label="failure-icon">❌</span>
          <span className="ml-2">Selected contact has no phone number for SMS.</span>
        </div>,
        toastOptions
      )
      return
    }

    const emailOptions = {
      to: currentContact.value,
      cc: contactsToBeCc,
      additional_cc: additionalCc,
      message,
      subject,
      file_ids: attachFilesToEmail ? selectedFileIds : undefined,
    }

    sendReleasedFiles(baseUrl, emailOptions, message, jobService.id, file.data.id)
      .then(res => {
        if (res.status !== 200) {
          toast.error(
            <div>
              <span role="img" aria-label="failure-icon">❌</span>
              <span className="ml-2">{res?.error?.response?.data?.errors[0]?.detail || "Failed to send email"}</span>
            </div>,
            toastOptions
          )
          return null
        }

        if (!sendSms) {
          toast.success(
            <div>
              <span role="img" aria-label="success-icon">✅</span>
              <span className="ml-2">Email Sent To Client!</span>
            </div>,
            toastOptions
          )
          window.location.reload()
          return null
        }

        return sendReleasedFilesSms(
          baseUrl,
          { to: currentContact.value },
          smsMessage,
          jobService.id,
          file.data.id
        )
      })
      .then(smsRes => {
        if (!smsRes) return

        if (smsRes.status === 200) {
          toast.success(
            <div>
              <span role="img" aria-label="success-icon">✅</span>
              <span className="ml-2">Email Sent & SMS Queued To Client!</span>
            </div>,
            toastOptions
          )
          window.location.reload()
        } else {
          toast.error(
            <div>
              <span role="img" aria-label="failure-icon">❌</span>
              <span className="ml-2">{smsRes?.error?.response?.data?.errors[0]?.detail || "Email sent, but SMS failed"}</span>
            </div>,
            toastOptions
          )
        }
      })
      .catch(err => {
        toast.error(
          <div>
            <span role="img" aria-label="failure-icon">❌</span>
            <span className="ml-2">{err?.response?.data?.errors[0]?.detail}</span>
          </div>,
          toastOptions
        )
      })
  }

  useEffect(() => {
    setSubject(`${displayJobNo} - ${jobService.jobTitle}`)
  }, [jobService, displayJobNo])

  useEffect(() => {
    // If the Current Selected Contact is IN the CC List, Remove them
    const newDisplayedCcContacts = ccContactOptionsForModal.map(jobContact => ({ value: jobContact.value, label: jobContact.label }))
    if (newDisplayedCcContacts.some(jobContact => jobContact.value === currentContact.value)) {
      const updatedCcList = newDisplayedCcContacts.filter(jobContact => jobContact.value !== currentContact.value)
      setDisplayedCcContacts(updatedCcList)
    }
  }, [currentContact, ccContactOptionsForModal])


  useEffect(() => {
    const mainPersons = toContactOptions.filter(jobContact => jobContact.contactType === "Main Person")
    const invoicePersons = toContactOptions.filter(jobContact => jobContact.contactType === "Invoice")
    const defaultOption = invoicePersons.length ? invoicePersons[0] : mainPersons[0]
    if (defaultOption) {
      setCurrentContact({ value: defaultOption.value, label: defaultOption.label })
    } else {
      setCurrentContact({})
    }
  }, [toContactOptions])

  const onSubmit = () => {
    setLoading(true)
    sendReleasedFilesHandler()
    onHide()
  }

  useEffect(() => {
    if (currentContact && currentContact.value) {
      const contact = toContactOptions.find(c => c.value === currentContact.value)
      if (contact) {
        setMessage(emailTemplate.replace("{{ToName}}", contact.fullName))
        setSmsMessage(smsTemplate(contact.fullName))
      }
    }
  }, [currentContact])

  useEffect(() => {
    if (show) {
      setLoading(false)
      setDisplayedCcContacts(ccContactOptionsForModal.length ? ccContactOptionsForModal.map(jobContact => ({ value: jobContact.value, label: jobContact.label })) : [])
      setSendSms(false)
      setCurrentContact(defaultOption ? { value: defaultOption.value, label: defaultOption.label } : {})
      if (attachFilesToEmail) {
        setSelectedFileIds(releasedFileOptions.length
          ? releasedFileOptions.map(releasedFile => releasedFile.id)
          : (file?.data?.id ? [file.data.id] : []))
      }
      setMessage(emailTemplate)
      setSmsMessage(smsTemplate(defaultOption?.fullName))
    }
  }, [show])


  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header
        className="pb-3"
        closeButton>
        <Modal.Title>
          <h3 className="modal-title">Choose Who to Send To</h3>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-md-5">
        <div className="form-group row">
          <label className="col-2 col-form-label">To</label>
          <div className="col-10">
            <Select
              className="assign-dropdown w-75"
              name="client"
              isSearchable
              placeholder="Select Client"
              options={toContactOptionsList.length ? toContactOptionsList : []}
              value={currentContact}
              onChange={e => {
                setCurrentContact(e);
              }} />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-2 col-form-label">CC</label>
          <form className="col-10">
            <div className="mb-4">
              {!!displayedCcContacts?.length && displayedCcContacts.map(contact => (
                <div className="custom-control custom-checkbox mb-2" key={contact.value}>
                  <input
                    className="custom-control-input"
                    id={contact.value}
                    type="checkbox"
                    onChange={e => handleContacts(e, contact)} />
                  <label className="custom-control-label" htmlFor={contact.value}>{contact.label}</label>
                </div>
              ))}
            </div>
          </form>
        </div>

        <div className="form-group row">
          <div className="col-2"></div>
          <div className="col-10">
            <label htmlFor="email">Additional Non-Contacts Cc</label>
            <input
              name="additional_cc"
              className="form-control"
              type="email"
              id="additional_cc"
              placeholder="Email"
              onChange={e => setAdditionalCc(e.target.value)} />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-2 col-form-label">Subject</label>
          <div className="col-10">
            <input
              name="Subject"
              className="form-control"
              value={subject}
              onChange={e => setSubject(e.target.value)} />
          </div>
        </div>

        {attachFilesToEmail && (
          <div className="form-group row">
            <label className="col-2 col-form-label">Files</label>
            <form className="col-10">
              <div className="mb-4">
                {!!releasedFileOptions?.length && releasedFileOptions.map(releasedFile => (
                  <div className="mb-2" key={releasedFile.id}>
                    <div className="custom-control custom-checkbox">
                      <input
                        className="custom-control-input"
                        id={`released-file-${releasedFile.id}`}
                        type="checkbox"
                        checked={selectedFileIds.includes(releasedFile.id)}
                        onChange={e => handleFileSelection(e, releasedFile.id)} />
                      <label className="custom-control-label" htmlFor={`released-file-${releasedFile.id}`}>
                        {releasedFile.label}
                      </label>
                    </div>
                    {releasedFile.isZip && <ZipContentsPreview fileId={releasedFile.id} />}
                  </div>
                ))}
              </div>
            </form>
          </div>
        )}

        <div className="form-group">
          <ReactQuill
            value={message}
            onChange={value => setMessage(value)} />
        </div>

        {showSendSms && (
          <div className="form-group">
            <div className="custom-control custom-checkbox mb-2">
              <input
                className="custom-control-input"
                id="send-sms"
                type="checkbox"
                checked={sendSms}
                onChange={e => setSendSms(e.target.checked)} />
              <label className="custom-control-label" htmlFor="send-sms">Send SMS</label>
            </div>
            {sendSms && !selectedContactHasPhone && (
              <small className="text-danger d-block mb-2">
                Selected contact has no phone number. SMS cannot be sent.
              </small>
            )}
          </div>
        )}

        {showSendSms && sendSms && (
          <div className="form-group">
            <textarea
              className="form-control"
              rows={6}
              value={smsMessage}
              onChange={e => setSmsMessage(e.target.value)} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex align-items-center justify-content-between px-md-5">
        <button
          className="btn btn-blue btn-lg"
          onClick={onHide}
          type="button">
          Back
        </button>
        <button
          className="btn btn-red btn-lg"
          onClick={onSubmit}
          disabled={!toContactOptionsList?.length || loading || outstandingInvoiceFlagged || unmatchedInvoiceFlagged || (attachFilesToEmail && !selectedFileIds.length) || (sendSms && !selectedContactHasPhone)}
          type="submit">
          Send to Client
          {loading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
export default SendReleasedFilesEmailModal
